/**
 * First-run trust copy (issue #10): where the list comes from, and how old the
 * published generation is. Pure so the wording is unit-tested without a DOM.
 *
 * Freshness reads `EventsPage.lastSuccessfulPromotionAt`, which the API already
 * returns (see apps/api events-query / handleEvents). The epoch instant is the
 * unpublished sentinel (`new Date(0)`), not a real promotion.
 */

/** Publishers the product is built to read. Used when a page has no event rows yet. */
export const KNOWN_SOURCE_NAMES = [
  'GeekWire',
  'University of Washington',
  'Town Hall Seattle',
  'Seattle Public Library',
  'Hugo House',
] as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function dedupeNames(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of names) {
    const trimmed = name.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

/** Unique source labels in first-seen order, from the events on a page. */
export function collectSourceNames(
  events: readonly { sourceNames: readonly string[] }[],
): string[] {
  return dedupeNames(events.flatMap((event) => event.sourceNames));
}

function joinNames(names: readonly string[]): string {
  const first = names[0] ?? '';
  if (names.length <= 1) return first;
  const second = names[1] ?? '';
  if (names.length === 2) return `${first} and ${second}`;
  const last = names[names.length - 1] ?? '';
  return `${names.slice(0, -1).join(', ')}, and ${last}`;
}

function limitJoin(names: readonly string[], max: number): string {
  if (names.length <= max) return joinNames(names);
  const head = names.slice(0, Math.max(1, max - 1));
  const extra = names.length - head.length;
  const lead = head[0] ?? '';
  if (head.length === 1) return `${lead} and ${extra} more`;
  return `${head.join(', ')}, and ${extra} more`;
}

/**
 * One short provenance sentence. Real event sources win; otherwise the known
 * public calendars, so the first screen can explain itself before (or without)
 * a result row.
 */
export function formatSourcesLine(names: readonly string[], max = 5): string {
  const unique = dedupeNames(names);
  const fromResults = unique.length > 0;
  const list = fromResults ? unique : [...KNOWN_SOURCE_NAMES];
  const prefix = fromResults ? 'Sourced from' : 'Sourced from public calendars:';
  return `${prefix} ${limitJoin(list, max)}.`;
}

/**
 * Visible freshness label. Relative while the generation is recent ("Updated
 * 2 hours ago"); an absolute UTC date once it is two weeks old. Never throws.
 */
export function formatUpdatedAgo(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then) || then <= 0) return 'Not updated yet';
  const delta = now - then;
  if (delta < MINUTE_MS) return 'Updated just now';
  if (delta < HOUR_MS) {
    const minutes = Math.floor(delta / MINUTE_MS);
    return `Updated ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (delta < DAY_MS) {
    const hours = Math.floor(delta / HOUR_MS);
    return `Updated ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  const days = Math.floor(delta / DAY_MS);
  if (days < 14) return `Updated ${days} ${days === 1 ? 'day' : 'days'} ago`;
  const absolute = new Date(then).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `Updated ${absolute}`;
}

/** True when the instant is a real promotion, not the unpublished sentinel. */
export function isKnownPromotion(iso: string): boolean {
  const then = Date.parse(iso);
  return !Number.isNaN(then) && then > 0;
}
